// Product Form Location Module - 위치 관리 (GPS, 역지오코딩, 지도)
// 메인 product_form_controller.js에서 import하여 사용
// Native app: NativeBridge.GPS 사용 (Android 권한 다이얼로그 지원)
// Web browser: navigator.geolocation 사용

import { createLogger } from "../../lib/logger"
import { getI18nMessage } from '../../lib/i18n_helper'
import { NativeBridge } from "../../lib/native_bridge"

const log = createLogger('ProductFormLocation')

// Geocoding result cache - prevents duplicate API calls
export class GeocodingCache {
  constructor(maxSize = 50) {
    this.cache = new Map()
    this.maxSize = maxSize
  }

  get(lat, lng) {
    const key = `${parseFloat(lat).toFixed(6)},${parseFloat(lng).toFixed(6)}`
    return this.cache.get(key)
  }

  set(lat, lng, address) {
    const key = `${parseFloat(lat).toFixed(6)},${parseFloat(lng).toFixed(6)}`

    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value
      this.cache.delete(firstKey)
    }

    this.cache.set(key, { address, timestamp: Date.now() })
  }

  has(lat, lng) {
    const key = `${parseFloat(lat).toFixed(6)},${parseFloat(lng).toFixed(6)}`
    const cached = this.cache.get(key)

    // Cache valid for 1 hour
    if (cached && Date.now() - cached.timestamp < window.BusinessConstants.UITiming.GEOCODING_CACHE_TTL_MS) {
      return true
    }

    if (cached) {
      this.cache.delete(key)
    }
    return false
  }
}

// Global cache instance (shared across controller instances)
export const geocodingCache = new GeocodingCache()

// Race guard token for concurrent geocoding requests
let geocodeToken = 0

export const LocationMixin = {
  async reverseGeocode(latitude, longitude) {
    const lat = parseFloat(latitude)
    const lng = parseFloat(longitude)

    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      log.warn('Invalid coordinates', { latitude, longitude })
      return null
    }

    // Check cache first
    if (geocodingCache.has(lat, lng)) {
      log.debug('Using cached address')
      return geocodingCache.get(lat, lng).address
    }

    // Race guard: increment token for this request
    const myToken = ++geocodeToken

    // Show loading state
    const loadingMsg = getI18nMessage(
      this.element,
      'locationGeocodingLoading',
      '正在確認地址...'
    )
    this.updateLocationStatus(loadingMsg, true)

    try {
      // Self-hosted reverse geocode (coords → district/門牌) — no Google. Same endpoint as map-picker.
      const res = await fetch(`/api/v1/reverse_geocode?lat=${lat}&lng=${lng}`, { headers: { Accept: 'application/json' } })

      // Race guard: 다른 요청이 진행 중이면 무시
      if (myToken !== geocodeToken) {
        log.debug('Geocoding request superseded by newer request')
        return null
      }

      if (res.ok) {
        const address = (await res.json())?.data?.address
        if (address) {
          geocodingCache.set(lat, lng, address)
          log.debug('Reverse geocoded address', { address })
          return address
        }
      }
    } catch (error) {
      // Race guard: 다른 요청이 진행 중이면 에러 메시지 생략
      if (myToken !== geocodeToken) {
        return null
      }
      log.error('Reverse geocode error', error)
      const failedMsg = getI18nMessage(
        this.element,
        'locationGeocodingFailed',
        '地址查詢失敗，請手動輸入'
      )
      this.updateLocationStatus(failedMsg, false)
    }

    return null
  },

  async useCurrentLocation(event) {
    const button = event.currentTarget
    const fallbackLatitude = parseFloat(button.dataset.latitude)
    const fallbackLongitude = parseFloat(button.dataset.longitude)
    const fallbackAddress = button.dataset.address?.trim()

    // 🔄 로딩 상태 시작
    button.disabled = true
    button.classList.add('opacity-50', 'pointer-events-none')
    button.dataset.loading = 'true'

    const useFallback = async (message, success = false) => {
      if (!Number.isNaN(fallbackLatitude) && !Number.isNaN(fallbackLongitude)) {
        await this.applyLocation(fallbackLatitude, fallbackLongitude, fallbackAddress)
      }
      this.updateLocationStatus(message, success)
      // ✅ 로딩 상태 해제
      this.resetLoadingState(button)
    }

    if (!("geolocation" in navigator)) {
      const notSupportedMsg = getI18nMessage(
        this.element,
        'locationNotSupportedFallback',
        '不支援目前位置功能。已套用預設位置。'
      )
      await useFallback(notSupportedMsg, false)
      return
    }

    const fetchingMsg = getI18nMessage(
      this.element,
      'locationFetchingCurrent',
      '正在確認目前位置...'
    )
    this.updateLocationStatus(fetchingMsg, true)

    // ========================================
    // 🔥 네이티브 앱: NativeBridge.GPS 사용
    // Android WebView에서는 navigator.geolocation이 권한 다이얼로그를 트리거하지 않음
    // GpsBridgeHandler.kt가 Android 시스템 권한 요청을 처리
    // ========================================
    if (NativeBridge.isNative()) {
      try {
        log.info('Using NativeBridge.GPS for location request')
        const location = await NativeBridge.GPS.requestLocation({ accuracy: "high" })
        const { latitude, longitude } = location

        if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
          const coordsParseFailedMsg = getI18nMessage(
            this.element,
            'locationCoordsParseFailed',
            '無法解析目前位置座標。已套用預設位置。'
          )
          await useFallback(coordsParseFailedMsg, false)
          return
        }

        await this.applyLocation(latitude, longitude)

        // 지도 마커 업데이트
        this.updateMapPickerMarker(latitude, longitude)

        // ✅ 로딩 상태 해제
        this.resetLoadingState(button)

        // 🗺️ Auto-open map picker after GPS location applied
        this.openMapPicker()
        return

      } catch (error) {
        log.warn('Native GPS error', error)

        // 에러 타입별 구분 (권한 거부, GPS 비활성화, 일반 에러)
        const isPermissionDenied = error.message?.includes('PERMISSION_DENIED') ||
                                   error.message?.includes('permission')
        const isGpsDisabled = error.message?.includes('GPS_DISABLED') ||
                              error.message?.includes('disabled')

        let errorMsg
        if (isGpsDisabled) {
          // GPS 시스템이 꺼져 있는 경우 (사용자가 GPS 활성화 다이얼로그에서 '아니오' 선택)
          errorMsg = getI18nMessage(this.element, 'locationGpsDisabled', 'GPS已停用。請在設定中開啟位置服務。')
        } else if (isPermissionDenied) {
          // 앱에 위치 권한이 거부된 경우
          errorMsg = getI18nMessage(this.element, 'locationPermissionDenied', '位置權限被拒絕。請在設定中允許位置存取。')
        } else {
          // 기타 에러 (네트워크, 타임아웃 등)
          errorMsg = getI18nMessage(this.element, 'locationFetchFailedFallback', '無法取得目前位置。已套用預設位置。')
        }

        await useFallback(errorMsg, false)
        return
      }
    }

    // ========================================
    // 웹 브라우저: 기존 navigator.geolocation 사용
    // ========================================
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords

        if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
          const coordsParseFailedMsg = getI18nMessage(
            this.element,
            'locationCoordsParseFailed',
            '無法解析目前位置座標。已套用預設位置。'
          )
          await useFallback(coordsParseFailedMsg, false)
          return
        }

        await this.applyLocation(latitude, longitude)

        // 🔥 GPS 완료 후 지도 마커 자동 업데이트
        this.updateMapPickerMarker(latitude, longitude)

        // ✅ 로딩 상태 해제
        this.resetLoadingState(button)

        // Status is updated inside applyLocation based on geocoding result

        // 🗺️ Auto-open map picker after GPS location applied
        this.openMapPicker()
      },
      async (error) => {
        log.warn('geolocation error', error)
        const fetchFailedMsg = getI18nMessage(
          this.element,
          'locationFetchFailedFallback',
          '無法取得目前位置。已套用預設位置。'
        )
        await useFallback(fetchFailedMsg, false)
      },
      {
        enableHighAccuracy: true,
        timeout: window.BusinessConstants.UITiming.GPS_TIMEOUT_MS,
        maximumAge: window.BusinessConstants.UITiming.GPS_MAX_AGE_MS
      }
    )
  },

  openMapPicker() {
    // Scroll to map preview section
    const mapContainer = document.querySelector("[data-controller~='map-picker']")
    if (mapContainer) {
      // 🗺️ 지도 컨테이너 확대 (h-64 → h-96)
      const mapElement = mapContainer.querySelector('[data-map-picker-target="container"]')
      if (mapElement) {
        mapElement.classList.remove('h-64')
        mapElement.classList.add('h-96', 'transition-all', 'duration-300')
      }

      // 스크롤 + 포커스
      mapContainer.scrollIntoView({ behavior: "smooth", block: "center" })

      // 🎯 명확한 안내 메시지 (아이콘 추가)
      const mapPickerHintMsg = getI18nMessage(
        this.element,
        'locationMapPickerHint',
        '🗺️ 請在下方地圖拖曳標記以選擇正確位置。'
      )
      this.updateLocationStatus(mapPickerHintMsg, true)

      // 마커 바운스 애니메이션으로 주목도 높이기
      setTimeout(() => {
        const mapPickerElement = document.querySelector("[data-controller~='map-picker']")
        if (mapPickerElement) {
          const mapPickerController = this.application.getControllerForElementAndIdentifier(
            mapPickerElement,
            "map-picker"
          )

          // Advanced Markers는 setAnimation을 지원하지 않으므로 CSS 애니메이션 사용
          if (mapPickerController?.marker?.content) {
            const markerContent = mapPickerController.marker.content
            markerContent.classList.add('animate-bounce')
            setTimeout(() => {
              markerContent.classList.remove('animate-bounce')
            }, window.BusinessConstants.UITiming.MARKER_BOUNCE_DURATION_MS)
          }
        }
      }, window.BusinessConstants.UITiming.SCROLL_COMPLETE_DELAY_MS)
    } else {
      const mapPickerUnavailableMsg = getI18nMessage(
        this.element,
        'locationMapPickerUnavailable',
        '地圖選擇位置功能尚在準備中。請先使用地址輸入。'
      )
      this.updateLocationStatus(mapPickerUnavailableMsg, false)
    }
  },

  // Reset loading state on button (DRY helper for GPS/fallback)
  resetLoadingState(button) {
    button.disabled = false
    button.classList.remove('opacity-50', 'pointer-events-none')
    button.dataset.loading = 'false'
  },

  // Update map picker marker position (DRY helper for native/web GPS and autocomplete)
  updateMapPickerMarker(latitude, longitude, address = null) {
    const lat = parseFloat(latitude)
    const lng = parseFloat(longitude)

    // Validate coordinates before updating map
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      log.warn('Invalid coordinates for map update', { latitude, longitude })
      return
    }

    const mapPickerElement = document.querySelector("[data-controller~='map-picker']")
    if (mapPickerElement) {
      const mapPickerController = this.application.getControllerForElementAndIdentifier(
        mapPickerElement,
        "map-picker"
      )

      if (mapPickerController?.updateMarker) {
        // Pass address to preserve it in the event (for autocomplete selection)
        mapPickerController.updateMarker(lat, lng, address)
        // 지도 중심도 새 위치로 이동
        if (mapPickerController.map) {
          mapPickerController.map.setCenter({ lat, lng })
        }
      }
    }
  },

  syncLocationDisplay() {
    if (this.hasLatitudeDisplayTarget && this.hasLatitudeFieldTarget) {
      this.latitudeDisplayTarget.textContent = this.latitudeFieldTarget.value || "—"
    }

    if (this.hasLongitudeDisplayTarget && this.hasLongitudeFieldTarget) {
      this.longitudeDisplayTarget.textContent = this.longitudeFieldTarget.value || "—"
    }
  },

  updateLocationStatus(message, success = false) {
    if (!this.hasLocationStatusTarget) return
    this.locationStatusTarget.textContent = message
    this.locationStatusTarget.classList.toggle("text-green-600", success)
    this.locationStatusTarget.classList.toggle("text-charcoal-500", !success)
  },

  // Briefly highlight the address field so it's clear the system filled it from the marker — the value is
  // a SUGGESTION the user can still edit (e.g. hand-type 樓/室). Pure CSS ring toggle; no value lock.
  flashAddressField() {
    if (!this.hasAddressFieldTarget) return
    const field = this.addressFieldTarget
    field.classList.add("ring-2", "ring-crust-400")
    if (this._addressFlashTimer) clearTimeout(this._addressFlashTimer)
    this._addressFlashTimer = setTimeout(() => field.classList.remove("ring-2", "ring-crust-400"), 900)
  },

  // Handle coordinate updates from map-picker controller (marker drag)
  async handleMapCoordinateChange(event) {
    const { latitude, longitude, address } = event.detail

    if (this.hasAddressFieldTarget) {
      // Use provided address if available, otherwise show loading state
      let displayAddress = address

      if (!displayAddress) {
        // Keep field empty, show loading status only
        const addressLoadingMsg = getI18nMessage(
          this.element,
          'locationAddressLoading',
          '地址確認中...'
        )
        this.updateLocationStatus(addressLoadingMsg, true)
      } else {
        this.addressFieldTarget.value = displayAddress

        // Update status based on result
        if (displayAddress.includes(',') && displayAddress.split(',').length === 2 && !displayAddress.includes('區')) {
          const mapSelectedEnterAddressMsg = getI18nMessage(
            this.element,
            'locationMapSelectedEnterAddress',
            '已在地圖上選擇位置。請手動輸入地址。'
          )
          this.updateLocationStatus(mapSelectedEnterAddressMsg, false)
        } else {
          const mapSelectedMsg = getI18nMessage(
            this.element,
            'locationMapSelected',
            '已在地圖上選擇位置。'
          )
          this.updateLocationStatus(mapSelectedMsg, true)
        }
      }
    }

    this.syncLocationDisplay()
  },

  // Handle the reverse-geocoded address from the map-picker (debounced after a marker drag / map tap).
  // The endpoint now returns a `precision` tier ("house"/"street"/"district") so we give HONEST accuracy
  // feedback instead of silently filling a district-only string. The fill is a non-destructive suggestion:
  // the field stays fully editable (the user hand-types 樓/室 — the private 號樓室 layer is never auto-derived)
  // and briefly highlights so it's clear the system updated it.
  handleAddressResolved(event) {
    const { address, precision } = event.detail
    if (!this.hasAddressFieldTarget || !address) return

    this.addressFieldTarget.value = address
    this.flashAddressField()

    switch (precision) {
      case "house":
        this.updateLocationStatus(
          getI18nMessage(this.element, 'locationPrecisionHouse', '已帶入精確門牌，可再補上樓層／室'), true)
        break
      case "street":
        this.updateLocationStatus(
          getI18nMessage(this.element, 'locationPrecisionStreet', '已定位到街道，請補上門牌號碼'), false)
        break
      case "district":
        this.updateLocationStatus(
          getI18nMessage(this.element, 'locationPrecisionDistrict', '僅定位到行政區，請拖曳標記靠近或手動輸入詳細地址'), false)
        break
      default:
        this.updateLocationStatus(
          getI18nMessage(this.element, 'locationMapSelected', '已在地圖上選擇位置。'), true)
    }
  },

  // Handle place selection from address-autocomplete controller
  async handleAutocompleteSelection(event) {
    const { latitude, longitude, address, placeResult } = event.detail

    log.debug('Autocomplete place selected', { latitude, longitude, address })

    // Update coordinates
    if (this.hasLatitudeFieldTarget) this.latitudeFieldTarget.value = latitude
    if (this.hasLongitudeFieldTarget) this.longitudeFieldTarget.value = longitude

    // Update address field (already filled by Google Places, but ensure it's set)
    if (this.hasAddressFieldTarget) {
      this.addressFieldTarget.value = address
    }

    // Update map marker (using DRY helper)
    this.updateMapPickerMarker(latitude, longitude, address)

    // Update coordinate display
    this.syncLocationDisplay()

    // Show success message
    const autocompleteSelectedMsg = getI18nMessage(
      this.element,
      'locationAutocompleteSelected',
      '已選擇地址。可在地圖上調整正確位置。'
    )
    this.updateLocationStatus(autocompleteSelectedMsg, true)
  },

  async applyLocation(latitude, longitude, addressOverride = null) {
    const latValue = parseFloat(latitude)
    const lngValue = parseFloat(longitude)

    if (Number.isNaN(latValue) || Number.isNaN(lngValue)) return

    const latString = latValue.toFixed(6)
    const lngString = lngValue.toFixed(6)

    if (this.hasLatitudeFieldTarget) this.latitudeFieldTarget.value = latString
    if (this.hasLongitudeFieldTarget) this.longitudeFieldTarget.value = lngString

    // Address field update with Reverse Geocoding
    if (this.hasAddressFieldTarget) {
      // Priority: 1) addressOverride 2) Geocoded address 3) Coordinates fallback
      let displayAddress = addressOverride

      if (!displayAddress) {
        // Try reverse geocoding
        const geocodedAddress = await this.reverseGeocode(latString, lngString)
        displayAddress = geocodedAddress || ''
      }

      this.addressFieldTarget.value = displayAddress

      // Update status based on result
      if (!displayAddress) {
        // Geocoding failed - show error message
        const locationAppliedEnterAddressMsg = getI18nMessage(
          this.element,
          'locationGeocodingFailed',
          '地址查詢失敗，請手動輸入'
        )
        this.updateLocationStatus(locationAppliedEnterAddressMsg, false)
      } else {
        const locationAppliedMsg = getI18nMessage(
          this.element,
          'locationApplied',
          '✅ 已套用目前位置。請在地圖上確認位置！'
        )
        this.updateLocationStatus(locationAppliedMsg, true)

        // 🎉 성공 피드백 애니메이션
        if (this.hasAddressFieldTarget) {
          // 주소 필드 하이라이트 (녹색 테두리)
          this.addressFieldTarget.classList.add('ring-2', 'ring-green-500', 'transition-all', 'duration-300')
          setTimeout(() => {
            this.addressFieldTarget.classList.remove('ring-2', 'ring-green-500')
          }, window.BusinessConstants.UITiming.GEOLOCATION_SUCCESS_HIGHLIGHT_MS)
        }

        // 햅틱 피드백 (모바일)
        if (navigator.vibrate) {
          navigator.vibrate(window.BusinessConstants.UITiming.HAPTIC_FEEDBACK_MS)
        }
      }
    }

    // Update coordinate display
    this.syncLocationDisplay()
  }
}
